import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { schemas, type CreateClientRequest } from '@kreart/shared';
import { FormErrorBanner } from '@/components/form';
import { useCreateClient } from '@/features/clients/hooks';
import { errorKeyFor } from '@/features/clients/errorMessages';
import { toastSuccess } from '@/lib/toast';
import { ClientFormPanel, type ClientFormValues } from './ClientFormPanel';

export function ClientCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateClient();

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(schemas.createClientRequest),
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      brand: '',
      codClient: '',
      contactName: '',
      email: '',
      phone: '',
      taxId: '',
      regCom: '',
      vatPayer: false,
      iban: '',
      bank: '',
      billingStreet: '',
      billingCity: '',
      billingCounty: '',
      billingCountry: 'RO',
      notes: '',
    },
  });

  const onCancel = () => {
    void navigate({ to: '/clients', search: {} });
  };

  const onSubmit = async (values: ClientFormValues) => {
    const body: CreateClientRequest = {
      firstName: values.firstName || undefined,
      lastName: values.lastName,
      company: values.company || undefined,
      brand: values.brand || undefined,
      codClient: values.codClient || undefined,
      contactName: values.contactName || undefined,
      email: values.email || undefined,
      phone: values.phone || undefined,
      taxId: values.taxId || undefined,
      regCom: values.regCom || undefined,
      vatPayer: values.vatPayer,
      iban: values.iban || undefined,
      bank: values.bank || undefined,
      billingStreet: values.billingStreet || undefined,
      billingCity: values.billingCity || undefined,
      billingCounty: values.billingCounty || undefined,
      billingCountry: values.billingCountry || 'RO',
      notes: values.notes || undefined,
    };
    try {
      await createMut.mutateAsync(body);
      toastSuccess('clients.create.created');
      void navigate({ to: '/clients', search: {} });
    } catch {
      /* surfaced via createMut.error */
    }
  };

  return (
    <ClientFormPanel
      form={form}
      title={t('clients.create.title')}
      submitLabel={t('clients.form.saveClientData')}
      isSubmitting={createMut.isPending}
      addressActionLabel={t('clients.addresses.addInline')}
      addressActionDisabled
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormErrorBanner error={createMut.error} resolveKey={errorKeyFor} />
    </ClientFormPanel>
  );
}
